/** SUNTRONIX ONE — CRM Vendor POs. Restricted to admin/management/procurement/finance. */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SupplierResponsePanel } from "@/pages/supplier/SupplierWorkspace";
import { Badge } from "@/components/ui/badge";
import { VtigerTable } from "@/components/app/VtigerTable";
import {
  ShoppingCart, Plus, RefreshCw, ChevronLeft, CheckCircle2, Trash2,
  Lock,
} from "lucide-react";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);

export default function VendorPOList() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [locked, setLocked] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/vtiger/vendor-pos");
      setRows(data.items || []);
      setLocked(false);
    } catch (e) {
      if (e.response?.status === 403) setLocked(true);
      else toast.error(e.response?.data?.detail || "Failed to load vendor POs");
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (locked) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center" data-testid="vpo-locked">
        <Lock className="h-10 w-10 text-slate-400 mx-auto mb-3" />
        <h2 className="text-lg font-semibold text-slate-900">Vendor POs are restricted</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
          Vendor POs contain internal cost & margin data. Only super_admin, management,
          procurement and finance roles can access this workspace.
        </p>
        <Link to="/commercial/crm" className="text-[12px] text-emerald-700 hover:underline mt-4 inline-block">
          ← Back to Commercial Workspace
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-[1500px]" data-testid="crm-vpo-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/commercial/crm" className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Commercial Workspace
          </Link>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-1 flex items-center gap-2">
            <ShoppingCart className="h-6 w-6 text-emerald-700" />
            Vendor POs
          </h1>
          <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1">
            <Lock className="h-3 w-3" /> Contains cost & margin data — never exposed to customers.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={busy}>
            <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} /> Refresh
          </Button>
          <Button onClick={() => setShowNew(true)}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="vpo-new">
            <Plus className="h-4 w-4 mr-1.5" /> New Vendor PO
          </Button>
        </div>
      </div>

      <VtigerTable testid="vendor-pos-table" rows={rows} pageSize={20}
        viewKey="vendor-pos" onRowClick={(r) => setSelected(r)}
        columns={[
          { key: "code", label: "PO #", mono: true, width: 150,
            render: (r) => <span className="font-mono font-semibold text-sky-700">{r.code}</span> },
          { key: "status", label: "Status", width: 110,
            render: (r) => <Badge className="text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700">{r.status}</Badge> },
          { key: "vendor_name", label: "Vendor",
            render: (r) => <span className="font-medium text-slate-800">{r.vendor_name}</span> },
          { key: "vendor_gstin", label: "GSTIN", mono: true, width: 150 },
          { key: "lines", label: "Lines", align: "right", num: true, width: 76,
            filter: false, value: (r) => (r.items || []).length },
          { key: "created_at", label: "Created", width: 100,
            value: (r) => (r.created_at || "").slice(0, 10) },
          { key: "grand_total_inr", label: "Total cost", align: "right", num: true, width: 120,
            value: (r) => r.grand_total_inr, render: (r) => fmtINR(r.grand_total_inr) },
        ]} />

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setSelected(null)}
          data-testid="vpo-detail-drawer">
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl bg-white h-full overflow-auto p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-heading font-semibold flex items-center gap-2">
                <span className="font-mono">{selected.code}</span>
                <Badge className="text-[10px] border-emerald-200 bg-emerald-50 text-emerald-700">{selected.status}</Badge>
              </h2>
              <button onClick={() => setSelected(null)} className="text-slate-500 text-[13px]"
                data-testid="vpo-drawer-close">Close ✕</button>
            </div>
            <div className="mt-3 text-[13px] text-slate-700">
              Vendor: <span className="font-medium">{selected.vendor_name}</span>
              {selected.vendor_gstin && <span className="text-slate-500"> · GSTIN {selected.vendor_gstin}</span>}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              {(selected.items || []).length} line(s) · created {new Date(selected.created_at).toLocaleDateString()}
              {" · "}Total <span className="font-mono font-semibold">{fmtINR(selected.grand_total_inr)}</span>
            </div>
            {(selected.items || []).length > 0 && (
              <div className="mt-4 border border-slate-200 rounded-md divide-y">
                {(selected.items || []).map((it, i) => (
                  <div key={i} className="p-2.5 flex items-center gap-3 text-[12px]">
                    <span className="flex-1 font-medium text-slate-800">{it.product_name}</span>
                    <span className="text-slate-500">×{it.quantity}</span>
                    <span className="font-mono">{fmtINR(it.unit_cost_inr)}</span>
                  </div>))}
              </div>)}
            <div className="mt-5">
              <SupplierResponsePanel poId={selected.id} />
            </div>
          </div>
        </div>
      )}

      {showNew && (
        <NewVPODrawer onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }} />
      )}
    </div>
  );
}


function NewVPODrawer({ onClose, onCreated }) {
  const [vendorName, setVendorName] = useState("");
  const [gstin, setGstin] = useState("");
  const [linkedCPO, setLinkedCPO] = useState("");
  const [customerPOs, setCustomerPOs] = useState([]);
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([
    { product_name: "", quantity: 1, unit_cost_inr: 0, tax_pct: 18 },
  ]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/vtiger/customer-pos").then(({ data }) =>
      setCustomerPOs(data.items || []));
  }, []);

  const addLine = () => setItems((s) => [...s, {
    product_name: "", quantity: 1, unit_cost_inr: 0, tax_pct: 18,
  }]);
  const rmLine = (i) => setItems((s) => s.filter((_, ix) => ix !== i));
  const upd = (i, k, v) => setItems((s) => s.map((it, ix) =>
    ix === i ? { ...it, [k]: v } : it));

  const total = items.reduce((acc, it) => {
    const sub = Number(it.quantity || 0) * Number(it.unit_cost_inr || 0);
    const tax = sub * (Number(it.tax_pct || 0) / 100);
    return acc + sub + tax;
  }, 0);

  const submit = async () => {
    if (!vendorName || items.length === 0) {
      toast.error("Vendor name and at least one line are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/vtiger/vendor-pos", {
        vendor_name: vendorName, vendor_gstin: gstin || null,
        linked_customer_po_id: linkedCPO || null,
        notes: notes || null,
        items: items.map((it) => ({ ...it,
          quantity: Number(it.quantity),
          unit_cost_inr: Number(it.unit_cost_inr),
          tax_pct: Number(it.tax_pct || 18),
        })),
      });
      toast.success("Vendor PO created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}
      data-testid="new-vpo-drawer">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-heading font-semibold">New Vendor PO</h2>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Field label="Vendor name *">
            <Input value={vendorName} onChange={(e) => setVendorName(e.target.value)}
              data-testid="new-vpo-vendor" />
          </Field>
          <Field label="Vendor GSTIN">
            <Input value={gstin} onChange={(e) => setGstin(e.target.value)}
              data-testid="new-vpo-gstin" />
          </Field>
        </div>
        <Field label="Link to Customer PO (recommended for margin roll-up)">
          <select value={linkedCPO} onChange={(e) => setLinkedCPO(e.target.value)}
            className="w-full h-9 border border-slate-300 rounded-md text-[13px] px-2"
            data-testid="new-vpo-cpo">
            <option value="">— None —</option>
            {customerPOs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} · {c.customer_po_number} · ₹{Math.round(c.total_amount_inr).toLocaleString("en-IN")}
              </option>
            ))}
          </select>
        </Field>

        <div className="mt-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Line items</div>
            <Button size="sm" variant="outline" onClick={addLine}
              data-testid="new-vpo-add-line">
              <Plus className="h-3 w-3 mr-1" /> Add line
            </Button>
          </div>
          <div className="border border-slate-200 rounded-md divide-y">
            {items.map((it, i) => (
              <div key={i} className="p-3 grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-5 h-8 text-[12px]" placeholder="Product / SKU"
                  value={it.product_name}
                  onChange={(e) => upd(i, "product_name", e.target.value)} />
                <Input className="col-span-2 h-8 text-[12px]" placeholder="Qty"
                  type="number" value={it.quantity}
                  onChange={(e) => upd(i, "quantity", e.target.value)} />
                <Input className="col-span-2 h-8 text-[12px]" placeholder="Unit cost"
                  type="number" value={it.unit_cost_inr}
                  onChange={(e) => upd(i, "unit_cost_inr", e.target.value)} />
                <Input className="col-span-2 h-8 text-[12px]" placeholder="Tax %"
                  type="number" value={it.tax_pct}
                  onChange={(e) => upd(i, "tax_pct", e.target.value)} />
                <button onClick={() => rmLine(i)} className="col-span-1 text-rose-600 hover:text-rose-800">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <Field label="Notes"><Textarea rows={2} value={notes}
          onChange={(e) => setNotes(e.target.value)} /></Field>

        <div className="mt-5 border-t pt-3 flex justify-between text-[16px] font-semibold">
          <span>Total (incl. tax)</span>
          <span className="font-mono" data-testid="new-vpo-total">{fmtINR(total)}</span>
        </div>

        <div className="mt-5 flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 h-10">Cancel</Button>
          <Button onClick={submit} disabled={busy}
            className="flex-1 h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="new-vpo-submit">
            <CheckCircle2 className="h-4 w-4 mr-1.5" />
            {busy ? "Creating…" : "Create Vendor PO"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mt-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
